/**
 * Filter Evaluation Utilities
 * 
 * Utilities for evaluating field filter conditions against data objects.
 * Used to apply booking type filters to projects and cost centres.
 */

import { FieldFilterCondition, FieldFilterConfig } from '../types';

/**
 * Resolves the field value from an item, handling customField: prefixed fields
 * by looking up `item.projectCustomFields[fieldId]`.
 */
function resolveFieldValue(item: Record<string, unknown>, fieldName: string): string {
  if (fieldName.startsWith('customField:')) {
    const fieldId = fieldName.slice('customField:'.length);
    const customFields = item['projectCustomFields'] as Record<string, unknown> | undefined;
    if (customFields && fieldId in customFields) {
      return customFields[fieldId]?.toString() ?? '';
    }
    return '';
  }
  return item[fieldName]?.toString() ?? '';
}

/**
 * Evaluates a single filter condition against an item
 * @param item - The data object to evaluate (e.g., Project or CostCentre)
 * @param condition - The filter condition to apply
 * @returns true if the item matches the condition, false otherwise
 */
export function evaluateFilterCondition(item: Record<string, unknown>, condition: FieldFilterCondition): boolean {
  const fieldValue = resolveFieldValue(item, condition.fieldName);
  const filterValue = condition.value || '';
  
  const fieldValueLower = fieldValue.toLowerCase();
  const filterValueLower = filterValue.toLowerCase();
  
  switch (condition.operator) {
    case 'equals':
      return fieldValueLower === filterValueLower;
      
    case 'not-equals':
      return fieldValueLower !== filterValueLower;
      
    case 'contains':
      return fieldValueLower.includes(filterValueLower);
      
    case 'not-contains':
      return !fieldValueLower.includes(filterValueLower);
      
    case 'starts-with':
      return fieldValueLower.startsWith(filterValueLower);
      
    case 'ends-with':
      return fieldValueLower.endsWith(filterValueLower);
      
    case 'is-empty':
      return fieldValue === '';
      
    case 'is-not-empty':
      return fieldValue !== '';

    case 'greater-than': {
      const numField = parseFloat(fieldValue);
      const numFilter = parseFloat(filterValue);
      if (isNaN(numField) || isNaN(numFilter)) {
        return fieldValueLower > filterValueLower;
      }
      return numField > numFilter;
    }

    case 'less-than': {
      const numField = parseFloat(fieldValue);
      const numFilter = parseFloat(filterValue);
      if (isNaN(numField) || isNaN(numFilter)) {
        return fieldValueLower < filterValueLower;
      }
      return numField < numFilter;
    }
      
    default:
      console.warn(`Unknown filter operator: ${condition.operator}`);
      return true;
  }
}

/**
 * Evaluates all filter conditions against an item
 * @param item - The data object to evaluate
 * @param filterConfig - The filter configuration with conditions and logic operator
 * @returns true if the item matches the filter configuration, false otherwise
 */
export function evaluateFilter(item: Record<string, unknown>, filterConfig: FieldFilterConfig): boolean {
  if (!filterConfig.enabled || !filterConfig.conditions || filterConfig.conditions.length === 0) {
    return true;
  }
  
  const results = filterConfig.conditions.map(condition => 
    evaluateFilterCondition(item, condition)
  );
  
  if (filterConfig.logicOperator === 'OR') {
    return results.some(result => result);
  } else {
    return results.every(result => result);
  }
}

/**
 * Filters an array of items based on a filter configuration
 * @param items - Array of items to filter
 * @param filterConfig - The filter configuration to apply
 * @returns Filtered array of items
 */
export function applyFilterToArray<T extends Record<string, unknown>>(items: T[], filterConfig?: FieldFilterConfig): T[] {
  if (!filterConfig || !filterConfig.enabled) {
    return items;
  }
  
  return items.filter(item => evaluateFilter(item, filterConfig));
}
